/**
 * The campaign pages' URL — `docs/adr/6017`.
 *
 * Three pages, one function each (client rule 3, as Simulate's, ADR 6007's amendment):
 *
 *   /inseason/campaigns?runId=&scenario=&campaignSimulation=
 *                                               COMPOSE — the question, launched into a branch
 *   /inseason/campaigns/history?runId=          HISTORY — the base run's campaign simulations
 *   /inseason/campaigns/:simulationId           RESULTS — one simulation's answer
 *
 * CLAUDE.md §3.4, with its two named exceptions and nothing else: the base run is DEC-99's
 * (the newest replenishment run — a campaign's base demand is the run's recorded
 * pull forecast, bata-platform ADR 6065 §3), and the scenario is ADR 6013's (the market's sandbox,
 * written into the URL). No date, channel, door, article or mechanic is ever defaulted. The parse is
 * pure: a string in, never `window`.
 *
 * `?campaignSimulation=` is the simulation launched from Compose (owner ruling 2026-10-20): the
 * form stays, read-only while it runs, filled and editable when it failed or expired. Its own key —
 * not Simulate's `?simulation=` — because the rail carries every key from page to page, and a
 * campaign's id read as a stage simulation's would be a read of the wrong thing.
 */

import { RUN_ID_PARAM } from '../scope';
import { SCENARIO_PARAM } from '../simulate';

export const CAMPAIGNS_PATH = '/inseason/campaigns';
/** A static segment, so the router resolves it before `/inseason/campaigns/:simulationId`. */
export const CAMPAIGNS_HISTORY_PATH = `${CAMPAIGNS_PATH}/history`;
export const CAMPAIGN_SIMULATION_PARAM = 'campaignSimulation';

export interface CampaignScope {
  /** `null`: no run named. It does NOT mean "every run". */
  readonly runId: string | null;
  readonly scenarioId: string | null;
}

const stated = (params: URLSearchParams, name: string): string | null => {
  const value = params.get(name)?.trim() ?? '';
  return value === '' ? null : value;
};

/** The scope a search string states. Pure. */
export function readCampaignScope(search: string): CampaignScope {
  const params = new URLSearchParams(search);
  return { runId: stated(params, RUN_ID_PARAM), scenarioId: stated(params, SCENARIO_PARAM) };
}

/**
 * The campaign simulation launched from Compose, or `null`. Apart from `readCampaignScope` so that
 * only Compose reads it (the URL-parameter census follows each parser to the screens using it). Pure.
 */
export function readLaunchedCampaign(search: string): string | null {
  return stated(new URLSearchParams(search), CAMPAIGN_SIMULATION_PARAM);
}

/** The search string with the launched simulation set, or removed on `null`. Pure. */
export function withLaunchedCampaign(search: string, simulationId: string | null): URLSearchParams {
  const next = new URLSearchParams(search);
  if (simulationId === null) next.delete(CAMPAIGN_SIMULATION_PARAM);
  else next.set(CAMPAIGN_SIMULATION_PARAM, simulationId);
  return next;
}

/** Compose on a simulation's own question — its base run, its scenario, and itself («Modifica e rilancia»). Pure. */
export function campaignComposeAgainHref(simulation: { readonly baseRunId: string; readonly scenarioId: string; readonly id: string }): string {
  const query = new URLSearchParams({
    [RUN_ID_PARAM]: simulation.baseRunId,
    [SCENARIO_PARAM]: simulation.scenarioId,
    [CAMPAIGN_SIMULATION_PARAM]: simulation.id,
  });
  return `${CAMPAIGNS_PATH}?${query.toString()}`;
}

/** One campaign simulation's results page. */
export function campaignPath(simulationId: string): string {
  return `${CAMPAIGNS_PATH}/${encodeURIComponent(simulationId)}`;
}

/** The history page of a run (the run kept, nothing else). */
export function campaignHistoryHref(runId: string | null): string {
  return runId === null ? CAMPAIGNS_HISTORY_PATH : `${CAMPAIGNS_HISTORY_PATH}?${new URLSearchParams({ [RUN_ID_PARAM]: runId }).toString()}`;
}

/** The simulation a pathname names, or `null` — `history` is the history page, never an id. Pure. */
export function readCampaignDrill(pathname: string): string | null {
  const base = CAMPAIGNS_PATH.split('/').filter(Boolean);
  const segments = pathname.split('/').filter(Boolean);
  if (!base.every((segment, index) => segments[index] === segment)) return null;
  const rest = segments.slice(base.length);
  if (rest.length !== 1) return null;
  try {
    const id = decodeURIComponent(rest[0] ?? '').trim();
    return id === '' || id === 'history' ? null : id;
  } catch {
    return null;
  }
}
